import {
  Heading,
  TableView,
  View,
  TableHeader,
  Column,
  Cell,
  Row,
  TableBody,
  Flex,
  Text,
  Picker,
  Link,
  Item,
  TooltipTrigger,
  Tooltip,
  DateRangePicker,
  SearchField,
  ActionButton,
  DialogTrigger,
  Dialog,
  Content,
  Divider,
  CheckboxGroup,
  Checkbox,
  Button,
  MenuTrigger,
  Menu,
} from "@adobe/react-spectrum";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./CallbackDashboard.module.css";
import { getScheduledCallbackListApi } from "../../../src/api/session-api";
// import ReassignCallbackDialog from "./ReassignCallbackDialog";
import RescheduleCallbackDialog from "./RescheduleCallbackDialog";
import CancelConfirmationDialog from "./CancelConfirmationDialog";
import newIcon from "../../assets/new.svg";
import inProgressIcon from "../../assets/inprogress.svg";
import completedIcon from "../../assets/completed.svg";
import failedIcon from "../../assets/failed.svg";
import cancelledIcon from "../../assets/cancel.svg";
import totalIcon from "../../assets/total.svg";
import teamsIcon from "../../assets/teamsIcon.svg";
import callIcon from "../../assets/callIcon.svg";

import {
  CallbackStatus,
} from "./types";
import {
  CallbackListAPIResponse,
  CallbackObjectType,
} from "../../types/callback.type";
import {
  callbackStatus,
  callbackTableColumns, 
  STATUS_COLORS,
  callbackTypes,
  priority,
  entitlement,
  managerNames
} from "./constants";
import {
  getAmazonConnectUrlByContactId,
  getSafeUrl,
  formatDuration,
} from "../../../src/utils/utils";
import Timer from "./Timer";
import { parseDate, today, CalendarDate, getLocalTimeZone } from "@internationalized/date";
import { ColumnStaticSize } from "@react-types/table";
import Download from "@spectrum-icons/workflow/Download";
import Settings from "@spectrum-icons/workflow/Settings";
import { toast } from "react-toastify";
import * as XLSX from 'xlsx';
import { useSelector } from "react-redux";
import { RootState } from "../../store/index";
import More from "@spectrum-icons/workflow/More";
import { TELEMETRY_ERROR } from "../../constants/events";
import Emitter from "../../emitter";
import { API_CALLBACK_DASHBOARD } from "../../constants/events";
import { API_CALL_DIAL } from "../../constants/constants";
import storeRTK from "../../store/storeRTK";
import { setSelectedTab } from "../../actions/common";

// Interface for ContactLink props for better type safety
interface ContactLinkProps {
  value: string;
  isConnectContact?: boolean;
  index?: number;
}

// Moved outside of parent component
const ContactLink = React.memo(({
  value,
  isConnectContact = false,
  index
}: ContactLinkProps) => {
  // Calculate memoized values first, before any conditional returns
  const contactUrl = React.useMemo(() => 
    value && typeof value === 'string' ? getAmazonConnectUrlByContactId(value) : null
  , [value]);
  
  const safeUrl = React.useMemo(() => 
    contactUrl ? getSafeUrl(contactUrl) : null
  , [contactUrl]);
  
  const displayText = React.useMemo(() => {
    if (!value || typeof value !== 'string') return "-";
    
    if (isConnectContact) {
      return `Attempt ${index !== undefined ? index + 1 : ""}`;
    } else {
      // Prevent substring errors by checking length
      // return value.length > 8 ? `${value.substring(0, 8)}...` : value;
      return value;
    }
  }, [isConnectContact, index, value]);
  
  // Handle empty or invalid values after hooks are called
  if (!value || typeof value !== 'string') return <Text>-</Text>;
  
  // Use either Link or Text based on URL availability
  return safeUrl ? (
    <Link
      href={safeUrl}
      target="_blank"
      UNSAFE_className={isConnectContact ? styles.alternateLink : undefined}
    >
      {displayText}
    </Link>
  ) : (
    <Text>{displayText}</Text>
  );
});

const convertToTimestamp = (calendarDate: CalendarDate, isEndDate = false) => {
  let date = new Date(calendarDate.toDate(getLocalTimeZone()));
  if (isEndDate) {
    date.setHours(23, 59, 59, 999);
  }
  return Math.floor(date.getTime() / 1000);
};

const isScheduledWebCallback = (value?: CallbackObjectType) => `${value?.callbackType || ""}` === "SCHEDULED_WEB";

const normalizeRequestStatus = (status?: string) => {
  if (!status) return "";
  if (status.startsWith("FAILED")) return "FAILED";
  return status === "IN_PROGRESS" ? "INPROGRESS" : status;
};

const isCancelledCallback = (value?: CallbackObjectType) =>
  normalizeRequestStatus(value?.requestStatus) === "CANCELLED";

const isFailedCallback = (value?: CallbackObjectType) =>
  normalizeRequestStatus(value?.requestStatus) === "FAILED";

const isCompletedCallback = (value?: CallbackObjectType) =>
  normalizeRequestStatus(value?.requestStatus) === "COMPLETED";

const isRescheduledCallback = (value?: CallbackObjectType) =>
  normalizeRequestStatus(value?.requestStatus) === "RESCHEDULED" ||
  Boolean(value?.callbackAttributes?.RESCHEDULED);

const isCancelDisabledForWebMeeting = (value?: CallbackObjectType) =>
  !isScheduledWebCallback(value) ||
  isCompletedCallback(value) ||
  isCancelledCallback(value) ||
  isFailedCallback(value);

const isRescheduleDisabledForWebMeeting = (value?: CallbackObjectType) =>
  !isScheduledWebCallback(value) ||
  isCompletedCallback(value) ||
  isCancelledCallback(value) ||
  isFailedCallback(value) ||
  isRescheduledCallback(value);

const getDisplayCallbackType = (value?: CallbackObjectType) => {
  const callbackType = `${value?.callbackType || ""}`;
  if (callbackType === "SCHEDULED" || callbackType === "IMMEDIATE" || callbackType === "SCHEDULED_WEB") {
    return callbackType;
  }
  return callbackType ? callbackType : "-";
};

// Utility function to get color for callback status
const fetchColor = (status: CallbackStatus): string => {
  if (!status) return "";
  
  // Normalize status to handle inconsistencies
  const normalizedStatus = status === "IN_PROGRESS" ? "INPROGRESS" : status;
  return STATUS_COLORS[normalizedStatus] || "";
};

// Helper function to format call duration
const getFormattedCallLength = (startstamp: number, endstamp: number): string => {
  // Handle invalid or missing timestamps
  if (!startstamp || !endstamp || startstamp >= endstamp) return "N/A";
  
  // Calculate difference and format
  const differenceInSeconds = Math.max(0, endstamp - startstamp);
  const minutes = Math.floor(differenceInSeconds / 60);
  const seconds = differenceInSeconds % 60;
  
  // Optimize string creation with template literals
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

const getAttendeeInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

const formatAttendanceDateTime = (isoTime: string): string =>
  new Date(isoTime).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

const normalizeRoutingStatusText = (text: string): string =>
  text.replace(/<br\s*\/?>/gi, "\n").replace(/\\n/g, "\n");

const routingStatusTextStyle = { whiteSpace: "pre-line" as const };

const RoutingStatusCell = ({ value }: { value: CallbackObjectType }) => {
  const statusText = value?.routingCurrentStatus
    ? normalizeRoutingStatusText(value.routingCurrentStatus)
    : "-";
  const attendanceReport = value?.attendanceReport;
  const showAttendance =
    (isCompletedCallback(value) || isCancelledCallback(value)) &&
    Boolean(attendanceReport?.length);

  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const closeTimerRef = useRef<number | undefined>(undefined);
  const triggerRef = useRef<HTMLSpanElement>(null);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = undefined;
    }
  };

  const updatePosition = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPosition({ top: rect.top, left: rect.left + rect.width / 2 });
  };

  const handleOpen = () => {
    clearCloseTimer();
    updatePosition();
    setIsOpen(true);
  };

  const handleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => setIsOpen(false), 120);
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const syncPosition = () => updatePosition();
    window.addEventListener("scroll", syncPosition, true);
    window.addEventListener("resize", syncPosition);

    return () => {
      window.removeEventListener("scroll", syncPosition, true);
      window.removeEventListener("resize", syncPosition);
      clearCloseTimer();
    };
  }, [isOpen]);

  if (!showAttendance) {
    return <Text UNSAFE_style={routingStatusTextStyle}>{statusText}</Text>;
  }

  return (
    <>
      <Text UNSAFE_style={routingStatusTextStyle}>
        <span ref={triggerRef} onMouseEnter={handleOpen} onMouseLeave={handleClose}>
          {statusText}
        </span>
      </Text>
      {isOpen &&
        createPortal(
          <div
            className={styles.attendanceTooltipPopover}
            style={{ top: position.top, left: position.left }}
            onMouseEnter={clearCloseTimer}
            onMouseLeave={handleClose}
          >
            <div className={styles.attendanceTooltipHeader}>
              Meeting Attendance ({attendanceReport!.length})
            </div>
            <div className={styles.attendanceTooltipBody}>
              {attendanceReport!.map((entry, index) => (
                <div key={`${entry.email}-${index}`} className={styles.attendanceCard}>
                  <div className={styles.attendeeHeader}>
                    <div className={styles.attendeeAvatar}>{getAttendeeInitials(entry.name)}</div>
                    <div className={styles.attendeeInfo}>
                      <div className={styles.attendeeName}>{entry.name || ""}</div>
                      <div className={styles.attendeeEmail}>{entry.email || "customer" || ""}</div>
                    </div>
                  </div>
                  <div className={styles.attendanceDetails}>
                    {[
                      { label: "Joined", time: entry.joinTime },
                      { label: "Left", time: entry.disconnectTime },
                    ].map(({ label, time }) => (
                      <div key={label} className={styles.attendanceDetailRow}>
                        <span className={styles.attendanceDetailLabel}>{label}</span>
                        <span className={styles.attendanceDetailValue}>
                          {formatAttendanceDateTime(time)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <span className={styles.attendanceDurationBadge}>
                    Duration: {formatDuration(entry.joinTime, entry.disconnectTime)}
                  </span>
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

// Extract contact rendering logic outside of TableBody mapping
const renderContactRow = (
  contact: any, 
  contactIndex: number, 
  callbackContactId: string
) => {
  try {
    if (!contact || !contact.id) {
      return null;
    }
    
    const callLength = getFormattedCallLength(
      contact.initiationTimestamp || 0,
      contact.disconnectTimestamp || 0
    );
    
    return (
      <View
        key={`${contactIndex}-${callbackContactId}`}
        UNSAFE_style={{ marginBottom: "4px" }}
      >
        <TooltipTrigger delay={0}>
          <ContactLink
            value={contact.id}
            isConnectContact={true}
            index={contactIndex}
          />
          <Tooltip>{`Call duration: ${callLength}`}</Tooltip>
        </TooltipTrigger>
      </View>
    );
  } catch (error) {
    console.error("Error rendering contact row:", error);
    return null;
  }
};

const renderCaseId = (value: CallbackObjectType) => {
  const caseId = value?.callbackAttributes?.CASEID;
  const caseLink = value?.callbackAttributes?.C14E_CASE_LINK;
  if(caseLink) {
    return (
      <Link href={caseLink} target="_blank" UNSAFE_className={styles.c143link}>{caseId}</Link>
    );
  }
  return caseId || "-";
};


const getStatusText = (value: CallbackObjectType) => {
  if (value?.requestStatus === "NEW") {
    const attributes = [];
    if (value?.callbackAttributes?.RESCHEDULED) attributes.push(" RESCHEDULED ");
    if (value?.callbackAttributes?.CALLBACK_REASSIGNED) attributes.push(" REASSIGNED ");
    return value.requestStatus + (attributes.length ? ` (${attributes.join(",")})` : "");
  }

  return value?.requestStatus + (value?.callbackAttributes?.STATUS_MESSAGE ? ` (${value.callbackAttributes.STATUS_MESSAGE})` : "");
};

// Extract cell content rendering to reduce nesting
const renderCellContent = (value: CallbackObjectType, dataIndex: string, onRescheduleClick?: (callbackData: any) => void, onCancelClick?: (callbackData: any) => void, userEmail?: string) => {
  if (!value) return "-";
  
  try {
    if (dataIndex === "contactId") {
      return (
        <TooltipTrigger delay={0} containerPadding={0}>
          <ContactLink
            value={value.contactId || ""}
            isConnectContact={false}
          />
          <Tooltip>{value.contactId || ""}</Tooltip>
        </TooltipTrigger>
      );
    }
    
    if (dataIndex === "requestStatus") {
      return (
        <>
          <View
            UNSAFE_style={{
              height: 8,
              width: 8,
              borderRadius: 50,
              backgroundColor: fetchColor(
                value.requestStatus as CallbackStatus
              ),
              display: "inline-block",
              marginRight: 8,
            }}
          ></View>
          <Text>{getStatusText(value)}</Text>
        </>
      );
    }

    if(dataIndex === "callbackType") {
      const isScheduledWeb = isScheduledWebCallback(value);
      return <Flex direction={"row"} gap={"size-100"} alignItems={"center"}>
        {isScheduledWeb ? (
          <img src={teamsIcon} alt="Scheduled Web" style={{ width: "12px", height: "12px", transform: "scale(1.5)" }} />
        ) : (
          <img src={callIcon} alt="Call" style={{ width: "12px", height: "12px", transform: "scale(1)", filter: "brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(383%) hue-rotate(93deg) brightness(95%) contrast(92%)" }} />
        )}
        <Text>{getDisplayCallbackType(value)}</Text>
      </Flex>;
    }
    
    if (dataIndex === "customerName") {
      const isScheduledWeb = isScheduledWebCallback(value);
      if (isScheduledWeb) {
        return <Text>{value?.callbackAttributes?.CUSTOMER_NAME || "-"}</Text>;
      }
      const firstName = value?.callbackAttributes?.FIRSTNAME || "";
      const lastName = value?.callbackAttributes?.LASTNAME || "";
      return (
        <Text>
          {`${firstName} ${lastName}`.trim() || "-"}
        </Text>
      );
    }
    
    if (dataIndex === "orgId") {
      return (
        <Text>
          {value?.callbackAttributes?.ORGID || value?.callbackAttributes?.ORG_ID || "-"}
        </Text>
      );
    }

    if (dataIndex === "ORGNAME") {
      return <Text>{value?.callbackAttributes?.ORGNAME || "-"}</Text>;
    }
    
    if (dataIndex === "connectContacts") {
      if (!value?.connectContacts?.length) return "-";
      
      return value.connectContacts.map(
        (contact, contactIndex) => renderContactRow(contact, contactIndex, value.contactId || "")
      );
    }
    
    if (dataIndex === "userLdap") {
      return <Text>{value?.contactHandleBy || "-"}</Text>;
    }
    
    // if (dataIndex === "callbackTimeEpoch") {
    //   if (!value?.callbackTimeEpoch) return <Text>-</Text>;
    //   const dateTimeString = new Date(value.callbackTimeEpoch * 1000).toLocaleString();
    //   const [datePart, timePart] = dateTimeString.split(', ');
    //   return (
    //     <Flex direction="column" gap="size-25">
    //       <Text UNSAFE_style={{ fontSize: "13px", fontWeight: "500", color: "#374151", lineHeight: "1.2" }}>
    //         {datePart}
    //       </Text>
    //       <Text UNSAFE_style={{ fontSize: "12px", color: "#6B7280", fontFamily: "monospace", lineHeight: "1.2" }}>
    //         {timePart}
    //       </Text>
    //     </Flex>
    //   );
    // }

    if (dataIndex === "callbackTimeEpoch") {
      if (!value?.callbackTimeEpoch) return <Text>-</Text>;
      const dateTimeString = new Date(value.callbackTimeEpoch * 1000).toLocaleString();
      const [datePart, timePart] = dateTimeString.split(', ');
      return (
        <TooltipTrigger delay={0}>
          <ActionButton UNSAFE_style={{ backgroundColor: "transparent", border: "none"}}>
            <Flex direction="column" gap="size-25" >
              <Text UNSAFE_style={{fontSize: "11px", fontWeight: "500", color: "#374151",  textAlign: "left" }}>
                {datePart}
              </Text>
              <Text UNSAFE_style={{ fontSize: "11px", fontWeight: "500", color: "#374151", fontFamily: "monospace", }}>
                {timePart}
              </Text>
            </Flex>
          </ActionButton>
          <Tooltip>
           System Local Time
          </Tooltip>
        </TooltipTrigger>
      );
    }

    if (dataIndex === "callbackSurface") {
      return <Text>{value?.callbackAttributes?.CALLBACK_SURFACE || "-"}</Text>;
    }

    if (dataIndex === "timezone") {
      return <Text>{value?.callbackAttributes?.TIMEZONE || "-"}</Text>;
    }

    if (dataIndex === "caseId") {
      return (
        <Text>
          {renderCaseId(value)}
        </Text>
      );
    }

    if(dataIndex === "PRIORITYTEXT") {
      return <Text>{value?.callbackAttributes?.PRIORITYTEXT || "-"}</Text>;
    }

    if (dataIndex === "acceptTimeEpoch") {
      if (!value?.callbackAttributes?.CALLBACK_ANSWERED_TIME) return <Text>-</Text>;
      const dateTimeString = new Date(Number(value?.callbackAttributes?.CALLBACK_ANSWERED_TIME) * 1000).toLocaleString();
      const [datePart, timePart] = dateTimeString.split(', ');
      return (
        <TooltipTrigger delay={0}>
          <ActionButton UNSAFE_style={{ backgroundColor: "transparent", border: "none" }}>
            <Flex direction="column" gap="size-25">
              <Text UNSAFE_style={{display: "block", fontSize: "11px", fontWeight: "500", color: "#374151", textAlign: "left" }}>
                {datePart}
              </Text>
              <Text UNSAFE_style={{ display: "block", fontSize: "11px", fontWeight: "500", color: "#374151", fontFamily: "monospace", }}>
                {timePart}
              </Text>
            </Flex>
          </ActionButton>
          <Tooltip>
           System Local Time
          </Tooltip>
        </TooltipTrigger>
      );
    }

    if (dataIndex === "dialTimeEpoch") {
      if (!value?.callbackAttributes?.CALLBACK_FIRST_ATTEMPT_TIME) return <Text>-</Text>;
      const dateTimeString = new Date(Number(value?.callbackAttributes?.CALLBACK_FIRST_ATTEMPT_TIME) * 1000).toLocaleString();
      const [datePart, timePart] = dateTimeString.split(', ');
      return (
        <TooltipTrigger delay={0}>
          <ActionButton UNSAFE_style={{ backgroundColor: "transparent", border: "none" }}>
            <Flex direction="column" gap="size-25">
              <Text UNSAFE_style={{display: "block", fontSize: "11px", fontWeight: "500", color: "#374151", textAlign: "left" }}>
                {datePart}
              </Text>
              <Text UNSAFE_style={{ display: "block", fontSize: "11px", fontWeight: "500", color: "#374151", fontFamily: "monospace", }}>
                {timePart}
              </Text>
            </Flex>
          </ActionButton>
          <Tooltip>
           System Local Time
          </Tooltip>
        </TooltipTrigger>
      );
    }

    if (dataIndex === "callbackScheduleTime") {
      if (!value?.callbackScheduleTimeEpoch) return <Text>-</Text>;
      const dateTimeString = new Date(Number(value?.callbackScheduleTimeEpoch) * 1000).toLocaleString();
      const [datePart, timePart] = dateTimeString.split(', ');
      return (
        <TooltipTrigger delay={0}>
          <ActionButton UNSAFE_style={{ backgroundColor: "transparent", border: "none" }}>
            <Flex direction="column" gap="size-25">
              <Text UNSAFE_style={{display: "block", fontSize: "11px", fontWeight: "500", color: "#374151", textAlign: "left" }}>
                {datePart}
              </Text>
              <Text UNSAFE_style={{ display: "block", fontSize: "11px", fontWeight: "500", color: "#374151", fontFamily: "monospace", }}>
                {timePart}
              </Text>
            </Flex>
          </ActionButton>
          <Tooltip>
           System Local Time
          </Tooltip>
        </TooltipTrigger>
      );
    }
    
    if( dataIndex === "CRMQUEUE") {
      return <Text>{value?.callbackAttributes?.CRMQUEUE || "-"}</Text>;
    }

    if( dataIndex === "ASSIGNED_ENGINEER_FULLNAME") {
      return <Text>{value?.callbackAttributes?.ASSIGNED_ENGINEER_FULLNAME || "-"}</Text>;
    }

    if( dataIndex === "CALLBACK_REASSIGNED_TSE") {
      return <Text>{value?.callbackAttributes?.CALLBACK_REASSIGNED_TSE_FULLNAME ? value?.callbackAttributes?.CALLBACK_REASSIGNED_TSE_FULLNAME : value?.callbackAttributes?.ASSIGNED_ENGINEER_FULLNAME || "-"}</Text>;
    }

    if( dataIndex === "entitlement") {
      return <Text>{value?.callbackAttributes?.LEVEL || "-"}</Text>;
    }

    if( dataIndex === "tseManagerName") {
      return <Text>{value?.tseManagerName  || "-"}</Text>;
    }

    if (dataIndex === "routingCurrentStatus") {
      return <RoutingStatusCell value={value} />;
    }

    if (dataIndex === "phoneNumber") {
      const isScheduledWeb = isScheduledWebCallback(value);
      if (isScheduledWeb) {
        const meetingLink = value?.callbackAttributes?.CUSTOMER_WEB_MEETING_LINK;
        return meetingLink ? (
          <Link href={meetingLink} target="_blank" UNSAFE_className={styles.teamsLink}>
            Web Meeting
          </Link>
        ) : (
          <Text>Web Meeting</Text>
        );
      }
      
      // Phone number with separate Call button
      const phoneNumber = value?.phoneNumber;
      if (phoneNumber && phoneNumber !== "-") {
        const handlePhoneClick = () => {
          // Clean phone number (remove special characters except +)
          const cleanPhoneNumber = phoneNumber.replace(/[^\d+]/g, "").replace(/\+(\d)/g, "+$1");
          // Ensure the number starts with + if it has digits
          const formattedCleanNumber = cleanPhoneNumber && !cleanPhoneNumber.startsWith("+")
            ? `+${cleanPhoneNumber}`
            : cleanPhoneNumber;
          
          Emitter.emit(API_CALL_DIAL, { phoneNumber: formattedCleanNumber });
          storeRTK.dispatch(setSelectedTab("Call"));
        };
        
        return (
          <Flex direction="row" gap="size-100" alignItems="center" UNSAFE_style={{ whiteSpace: "nowrap" }}>
            <Text>{phoneNumber}</Text>
            <TooltipTrigger delay={0}>
              <ActionButton
                onPress={handlePhoneClick}
                aria-label="Dial Call"
                isQuiet
                UNSAFE_className={styles.callButton}
              >
                <img src={callIcon} alt="Dial Call" style={{ width: "12px", height: "12px" }} />
              </ActionButton>
              <Tooltip>Dial Call</Tooltip>
            </TooltipTrigger>
          </Flex>
        );
      }
      
      return <Text>{phoneNumber || "-"}</Text>;
    }

if (dataIndex === "editCallback") {
  const getDisabledMenuKeys = () => {
    const disabledKeys: string[] = ["reassign"];

    if (isCancelDisabledForWebMeeting(value)) {
      disabledKeys.push("cancel");
    }

    if (isRescheduleDisabledForWebMeeting(value)) {
      disabledKeys.push("reschedule");
    }

    return disabledKeys;
  };

  return (
    <MenuTrigger>
      <ActionButton 
        aria-label="Edit Callback"
        isQuiet
        UNSAFE_style={{ 
          minHeight: "32px",
          padding: "4px 8px"
        }}
      >
        <More />
      </ActionButton>
      
      <Menu onAction={(key) => {
        switch(key) {
          case 'reschedule':
            if (onRescheduleClick) {
              onRescheduleClick(value);
              Emitter.emit(TELEMETRY_ERROR, {
                type: "notif",
                chan: TELEMETRY_ERROR,
                body: {
                  type: API_CALLBACK_DASHBOARD,
                  info: "Rescheduled Callback button clicked by " + userEmail,
                },
              }); 
            }
            break;
          case 'cancel':
            if (onCancelClick) {
              onCancelClick(value);
              Emitter.emit(TELEMETRY_ERROR, {
                type: "notif",
                chan: TELEMETRY_ERROR,
                body: {
                  type: API_CALLBACK_DASHBOARD,
                  info: "Cancel Callback button clicked by " + userEmail,
                },
              });
            }
            break;
          // case 'reassign':
          //   if (onReassignClick) {
          //     onReassignClick(value);
          //     Emitter.emit(TELEMETRY_ERROR, {
          //       type: "notif",
          //       chan: TELEMETRY_ERROR,
          //       body: {
          //         type: API_CALLBACK_DASHBOARD,
          //         info: "Reassign Callback button clicked by " + userEmail,
          //       },
          //     });
          //   }
          //   break;
          default:
            break;
        }
      }} 
      disabledKeys={getDisabledMenuKeys()}
      >
        {/* <Item key="reassign" >
          Reassign
        </Item> */}
        <Item key="reschedule">
          Reschedule
        </Item>
        <Item key="cancel">
          Cancel
        </Item>
       
      </Menu>
    </MenuTrigger>
  );
}
    // Default case with safe handling of dynamic properties
    try {
      const value1 = value?.[dataIndex as keyof CallbackObjectType];
      if (value1 === undefined || value1 === null) return "-";
      return typeof value1 === 'object' 
        ? JSON.stringify(value1) 
        : String(value1);
    } catch (err) {
      console.error(`Error rendering cell content for ${dataIndex}:`, err);
      return "-";
    }
  } catch (error) {
    console.error(`Error in renderCellContent for ${dataIndex}:`, error);
    return "-";
  }
};

// using try catch to handle the error
const getCleanProductList = (productList: string) => {
  try {
    const jsonProductList = JSON.parse(productList);
    return jsonProductList;
  } catch (error) {
    console.error("Error getting clean product list:", error);  
    return [];
  }
};

const CallbackDashboard = () => {
  const initializeVisibleColumns = () => {
    const columnInLocalStorage = localStorage.getItem("savedColumns");
    if (columnInLocalStorage) {
      try {
        return JSON.parse(columnInLocalStorage);
      } catch (error) {
        console.error("Error parsing saved columns:", error);
      }
    }
    return callbackTableColumns.map(col => col.key);
  };

  const getTimeslots = (callbackBusinessHours: string) => {
    const [startTime, endTime] = callbackBusinessHours.split("-").map((time) => time.trim());
    const interval = 30;
    const timeslots = [];
    
    const parseTime = (timeStr: string) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };
    
    const formatTime = (minutes: number) => {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    };
    
    const startMinutes = parseTime(startTime);
    const endMinutes = parseTime(endTime);
    
    for (let minutes = startMinutes; minutes < endMinutes; minutes += interval) {
      const slotStart = formatTime(minutes);
      const slotEnd = formatTime(minutes + interval);
      const timeSlotWindow = `${slotStart}  -  ${slotEnd}`;
      
      const obj = {
        key: timeSlotWindow,
        label: timeSlotWindow,
        value: timeSlotWindow
      };
      timeslots.push(obj);
    }
    
    return timeslots;
  };

  const [visibleColumns, setVisibleColumns] = useState<string[]>(initializeVisibleColumns);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [tempVisibleColumns, setTempVisibleColumns] = useState<string[]>(visibleColumns);
  const [validColumns, setValidColumns] = useState<typeof callbackTableColumns>(() => {
    if (callbackTableColumns && Array.isArray(callbackTableColumns) && callbackTableColumns.length > 0) {
      return callbackTableColumns.filter(col => visibleColumns.includes(col.key));
    }
    return [];
  });
  const [fallbackProductList, setFallbackProductList] = useState<{ id: string, name: string }[]>([{ id: "ALL", name: "All" }]);
  const [managerList, setManagerList] = useState<{ id: string, name: string }[]>(managerNames);
  const product = useSelector((state: RootState) => state?.agent?.aicConfig?.available_products);
  const callbackBusinessHours = useSelector((state: RootState) => state?.agent?.aicConfig?.callback_business_hours) || "00:00:00-23:59:59";
  const timeslots = getTimeslots(callbackBusinessHours);
  const productList = product?.replace(/,(\s*[}\]])/g, '$1');
  const cleanedProductList = getCleanProductList(productList);
  const [selectedEntitlement, setSelectedEntitlement] = useState<string>(() => {
    return localStorage.getItem("callbackEntitlement") || "ALL";
  });
  // const [isReassignDialogOpen, setIsReassignDialogOpen] = useState(false);
  // const [selectedCallbackForReassign, setSelectedCallbackForReassign] = useState<any>(null);
  const [isRescheduleDialogOpen, setIsRescheduleDialogOpen] = useState(false);
  const [selectedCallbackForReschedule, setSelectedCallbackForReschedule] = useState<any>(null);
  const [showCancelConfirmDialog, setShowCancelConfirmDialog] = useState(false);
  const [selectedCallbackForCancel, setSelectedCallbackForCancel] = useState<any>(null); 
  const userEmail = useSelector((state: RootState) => state?.agent?.email);
  const userName = useSelector((state: RootState) => {
    const agent = state?.agent;
    const firstName = agent?.firstName || agent?.agentInfo?.firstName || "";
    const lastName = agent?.lastName || agent?.agentInfo?.lastName || "";
    return `${firstName} ${lastName}`.trim();
  });

  useEffect(() => {
    try {
      if (callbackTableColumns && Array.isArray(callbackTableColumns) && callbackTableColumns.length > 0) {
        const filteredColumns = callbackTableColumns.filter(col => visibleColumns.includes(col.key));
        setValidColumns(filteredColumns.length > 0 ? filteredColumns : callbackTableColumns);
      } else {
        console.error("callbackTableColumns is invalid:", callbackTableColumns);
      }
    } catch (error) {
      console.error("Error initializing columns:", error);      
    }
  }, [visibleColumns]);

  // Remove this state since CallbackDetails is commented out
  // const [showCallbackDetails, setShowCallbackDetails] = useState<boolean>(false);
  const [filteredCbs, setFilteredCbs] = useState<CallbackObjectType[]>([]);
  const [callbacks, setCallbacks] = useState<CallbackObjectType[]>([]);
  const [searchText, setSearchText] = useState<string>(() => {
    return localStorage.getItem("callbackSearchText") ?? "";
  });
  const [selectedCallbackStatus, setSelectedCallbackStatus] =
    useState<CallbackStatus | null>(() => {
      return (
        (localStorage.getItem("callbackStatus") as CallbackStatus) || "ALL"
      );
    });
  const [selectedCallbackType, setSelectedCallbackType] = useState<string>(() => {
    return localStorage.getItem("callbackType") || "ALL";
  });
  const [selectedPriority, setSelectedPriority] = useState<string>(() => {
    return localStorage.getItem("callbackPriority") || "ALL";
  });
  const [selectedProduct, setSelectedProduct] = useState<string>(() => {
    return localStorage.getItem("callbackProduct") || "ALL";
  });
  const [selectedManager, setSelectedManager] = useState<string>(() => {
    return localStorage.getItem("callbackManager") || "ALL";
  });
  const [isRotating, setIsRotating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const initialStartDate = today(getLocalTimeZone()).subtract({ days: 1 });
  const initialEndDate = today(getLocalTimeZone());
  const storedStart = localStorage.getItem("callbackStartDate");
  const storedEnd = localStorage.getItem("callbackEndDate");
  const defaultStartDate = storedStart
    ? parseDate(storedStart)
    : initialStartDate;
  const defaultEndDate = storedEnd ? parseDate(storedEnd) : initialEndDate;

  const [dateRange, setDateRange] = useState({
    start: defaultStartDate,
    end: defaultEndDate,
  });
  const [timestamps, setTimestamps] = useState({
    startTime: convertToTimestamp(defaultStartDate),
    endTime: convertToTimestamp(defaultEndDate, true),
  });

  useEffect(() => {
    getScheduledCallbackList();
  }, []);

  useEffect(() => {
    if(callbacks.length > 0) {
      const productList = callbacks.map((callback) => callback.productFamily);
      const uniqueProductList = [...new Set(productList)];
      const fallbackProductListIdName = uniqueProductList.map((product) => ({
        id: product,
        name: product,
      }));
      fallbackProductListIdName.unshift({
        id: "ALL",
        name: "All",
      });
      setFallbackProductList(fallbackProductListIdName);


      // this is for manager names filter
      const managerNames = callbacks.map((callback) => callback.tseManagerName).filter(Boolean);
      const uniqueManagerNames = [...new Set(managerNames)];
      const managerListIdName = uniqueManagerNames.map((manager) => ({
        id: manager,
        name: manager,
      }));
      managerListIdName.unshift({
        id: "ALL",
        name: "All",
      });
      setManagerList(managerListIdName);
    }
  }, [callbacks]);


  const handleDateChange = (
    range: { start: CalendarDate; end: CalendarDate } | null
  ) => {
    if (range) {
      setDateRange(range);
      const newStartTime = convertToTimestamp(range.start);
      const newEndTime = convertToTimestamp(range.end, true);
      
      setTimestamps({
        startTime: newStartTime,
        endTime: newEndTime,
      });
      
      localStorage.setItem("callbackStartDate", range.start.toString());
      localStorage.setItem("callbackEndDate", range.end.toString());
      
      getScheduledCallbackListApi(newStartTime, newEndTime)
        .then((res: CallbackListAPIResponse) => {
          // @ts-ignore
          const result = res.length > 0 ? res?.sort((a, b) => b.callbackTimeEpoch - a.callbackTimeEpoch) : [];
          setCallbacks(result);
          applyFilters(result, searchText, selectedCallbackStatus, selectedCallbackType, selectedPriority, selectedProduct, selectedManager, selectedEntitlement);
        })
        .catch((error) => {
          console.error("Failed to fetch scheduled callback details:", error);
        });
    }
  };
  
  const getScheduledCallbackList = async () => {
    setIsRotating(true);
    try {
      const res: CallbackListAPIResponse = await getScheduledCallbackListApi(
        timestamps.startTime,
        timestamps.endTime
      );
      // @ts-ignore
      const result = res.length > 0 ? res?.sort((a, b) => b.callbackTimeEpoch - a.callbackTimeEpoch) : [];
      setCallbacks(result);
      applyFilters(result, searchText, selectedCallbackStatus, selectedCallbackType, selectedPriority, selectedProduct, selectedManager, selectedEntitlement);
    } catch (error) {
      console.error("Failed to fetch scheduled callback details:", error);
    } finally {
      setIsRotating(false);
    }
  };

  const applyFilters = (data: CallbackObjectType[], searchValue: string, status: CallbackStatus | null, type: string = "ALL", priority: string = "ALL", product: string = "ALL", manager: string = "ALL", entitlement: string = "ALL") => {
    if (!data || !Array.isArray(data)) {
      setFilteredCbs([]);
      return;
    }
    
    // Always start with the original data
    const originalData = data;
    
    // Apply all filters at once to ensure they're combined properly
    const filtered = originalData.filter(item => {
      let matchesStatus = true;
      let matchesSearch = true;
      let matchesType = true;
      let matchesPriority = true;
      let matchesProduct = true;
      let matchesManager = true;
      let matchesEntitlement = true;
      
      // Status filter
      if (status && status !== "ALL") {
        const normalizedStatus = status === "IN_PROGRESS" ? "INPROGRESS" : status;
        const itemStatus = item.requestStatus === "IN_PROGRESS" ? "INPROGRESS" : item.requestStatus;
        
        // Handle FAILED status - match both "FAILED" and "FAILED (Invalid Number)" etc.
        if (normalizedStatus === "FAILED") {
          matchesStatus = itemStatus?.startsWith("FAILED") || false;
        } else {
          matchesStatus = itemStatus === normalizedStatus;
        }
      }
      
      // Type filter
      if (type && type !== "ALL") {
        matchesType = item.callbackType === type;
      }

      // Priority filter
      if (priority && priority !== "ALL") {
        matchesPriority = item.callbackAttributes?.PRIORITYTEXT === priority;
      }

      // Product filter
      if (product && product !== "ALL") {
        matchesProduct = item.productFamily === product;
      }

      // Manager filter
      if (manager && manager !== "ALL") {
        matchesManager = item.tseManagerName === manager;
      }

      // Entitlement filter
      if (entitlement && entitlement !== "ALL") {
        matchesEntitlement = item.callbackAttributes?.LEVEL === entitlement;
      }
      
      // Search filter
      const searchLower = searchValue?.trim().toLowerCase();
      if (searchLower) {
        // Safely access potentially undefined properties
        const firstName = item.callbackAttributes?.FIRSTNAME || "";
        const lastName = item.callbackAttributes?.LASTNAME || "";
        const userLdap = item.contactHandleBy || "";
        const orgId = item.callbackAttributes?.ORG_ID || item.callbackAttributes?.ORGID || "";
        
        // Create lowercase versions of strings once
        const contactIdLower = (item.contactId || "").toLowerCase();
        const phoneNumberLower = (item.phoneNumber || "").toLowerCase();
        const productFamilyLower = (item.productFamily || "").toLowerCase();
        const callbackTypeLower = (item.callbackType || "").toLowerCase();
        const requestStatusLower = (item.requestStatus || "").toLowerCase();
        const firstNameLower = firstName.toLowerCase();
        const lastNameLower = lastName.toLowerCase();
        const fullNameLower = `${firstNameLower} ${lastNameLower}`.trim();
        const userLdapLower = userLdap.toLowerCase();
        const orgIdLower = orgId.toLowerCase();
        const caseIdLower = (item.callbackAttributes?.CASEID || "").toLowerCase();
        const callbackSurfaceLower = (item.callbackAttributes?.CALLBACK_SURFACE || "").toLowerCase();
        const crmQueueLower = (item.callbackAttributes?.CRMQUEUE || "").toLowerCase();
        const assignedEngineerLower = (item.callbackAttributes?.ASSIGNED_ENGINEER || "").toLowerCase();
        const priorityLower = (item.callbackAttributes?.PRIORITYTEXT || "").toLowerCase();
        const entitlementLower = (item.callbackAttributes?.LEVEL || "").toLowerCase();
        const orgNameLower = (item.callbackAttributes?.ORGNAME || "").toLowerCase();
        const assignedEngineerFullNameLower = (item.callbackAttributes?.ASSIGNED_ENGINEER_FULLNAME || "").toLowerCase();
        const callbackReassignedTseLower = (item.callbackAttributes?.CALLBACK_REASSIGNED_TSE_FULLNAME || "").toLowerCase();
        const customerAdobeIdLower = (item.customerAdobeId || "").toLowerCase();
        const tseManagerLower = (item.tseManagerName  || "").toLowerCase();
        const routingCurrentStatusLower = (item.routingCurrentStatus || "").toLowerCase();
        
        // Safely create connectContacts string
        let allConnectContacts = "";
        if (item.connectContacts && Array.isArray(item.connectContacts)) {
          allConnectContacts = item.connectContacts
            .map(contact => contact.id || "")
            .filter(Boolean)
            .join(",");
        }

        // Check if any field contains the search term
        matchesSearch = (
          contactIdLower.includes(searchLower) ||
          phoneNumberLower.includes(searchLower) ||
          productFamilyLower.includes(searchLower) ||
          firstNameLower.includes(searchLower) ||
          lastNameLower.includes(searchLower) ||
          fullNameLower.includes(searchLower) ||
          callbackTypeLower.includes(searchLower) ||
          requestStatusLower.includes(searchLower) ||
          userLdapLower.includes(searchLower) ||
          allConnectContacts.includes(searchLower) ||
          orgIdLower.includes(searchLower) ||
          caseIdLower.includes(searchLower) ||
          callbackSurfaceLower.includes(searchLower) ||
          crmQueueLower.includes(searchLower) ||
          assignedEngineerLower.includes(searchLower) ||
          priorityLower.includes(searchLower) ||
          entitlementLower.includes(searchLower) ||
          orgNameLower.includes(searchLower) ||
          assignedEngineerFullNameLower.includes(searchLower) ||
          callbackReassignedTseLower.includes(searchLower) ||
          customerAdobeIdLower.includes(searchLower) ||
          tseManagerLower.includes(searchLower) ||
          routingCurrentStatusLower.includes(searchLower)
        );
      }
      
      // An item must match all filters to be included
      return matchesStatus && matchesSearch && matchesType && matchesPriority && matchesProduct && matchesManager && matchesEntitlement;
    });
    
    setFilteredCbs(filtered);
  };

  const handleSearch = (value: any) => {
    setSearchText(value);
    localStorage.setItem("callbackSearchText", value);
    applyFilters(callbacks, value, selectedCallbackStatus, selectedCallbackType, selectedPriority, selectedProduct, selectedManager, selectedEntitlement);
  };

  const handleStatusChange = (value: any) => {
    setSelectedCallbackStatus(value);
    localStorage.setItem("callbackStatus", value);
    applyFilters(callbacks, searchText, value, selectedCallbackType, selectedPriority, selectedProduct, selectedManager, selectedEntitlement);
  };

  const handleTypeChange = (value: any) => {
    setSelectedCallbackType(value);
    localStorage.setItem("callbackType", value);
    applyFilters(callbacks, searchText, selectedCallbackStatus, value, selectedPriority, selectedProduct, selectedManager, selectedEntitlement);
  };

  const handlePriorityChange = (value: any) => {
    setSelectedPriority(value);
    localStorage.setItem("callbackPriority", value);
    applyFilters(callbacks, searchText, selectedCallbackStatus, selectedCallbackType, value, selectedProduct, selectedManager, selectedEntitlement);
  };

  const handleProductChange = (value: any) => {
    setSelectedProduct(value);
    localStorage.setItem("callbackProduct", value);
    applyFilters(callbacks, searchText, selectedCallbackStatus, selectedCallbackType, selectedPriority, value, selectedManager, selectedEntitlement);
  };

  const handleManagerChange = (value: any) => {
    setSelectedManager(value);
    localStorage.setItem("callbackManager", value);
    applyFilters(callbacks, searchText, selectedCallbackStatus, selectedCallbackType, selectedPriority, selectedProduct, value, selectedEntitlement);
  };

  // Reassign dialog handlers
  // const handleReassignClick = (callbackData: any) => {
  //   setSelectedCallbackForReassign(callbackData);
  //   setIsReassignDialogOpen(true);
  // };

  // const handleReassignDialogClose = () => {
  //   setIsReassignDialogOpen(false);
  //   setSelectedCallbackForReassign(null);
  // };

  // const handleReassignConfirm = async () => {
  //   try {
  //     await getScheduledCallbackList();
  //   } catch (error) {
  //     console.error("Error reassigning callback:", error);
  //   }
  // };

  const handleRescheduleClick = (callbackData: any) => {
    // console.log("Reschedule line 829 callback for:", callbackData);
    setSelectedCallbackForReschedule(callbackData);
    setIsRescheduleDialogOpen(true);
  };

  const handleRescheduleDialogClose = () => {
    setIsRescheduleDialogOpen(false);
    setSelectedCallbackForReschedule(null);
  };

  const handleRescheduleConfirm = async () => {
    try {
      // console.log("Rescheduling callback:", rescheduleData);
      await getScheduledCallbackList();
    } catch (error) {
      console.error("Error rescheduling callback:", error);
    }
  };



  // Cancel confirmation dialog handlers
  const handleCancelClick = (callbackData: any) => {
    setSelectedCallbackForCancel(callbackData);
    setShowCancelConfirmDialog(true);
  };

  const handleCancelConfirmDialogClose = () => {
    setShowCancelConfirmDialog(false);
    setSelectedCallbackForCancel(null);
  };


  const handleEntitlementChange = (value: any) => {
    setSelectedEntitlement(value);
    localStorage.setItem("callbackEntitlement", value);
    applyFilters(callbacks, searchText, selectedCallbackStatus, selectedCallbackType, selectedPriority, selectedProduct, selectedManager, value);
  };

  
/* Columns visibility related functions */
  const handleSettingsOpenAndClose = () => {
    setTempVisibleColumns([...visibleColumns]);
    setIsSettingsOpen(!isSettingsOpen);
  }; 

  const handleColumnToggle = (columnKeys: string[]) => {
    setTempVisibleColumns(columnKeys);
  };

  const handleSaveColumns = () => {    
    setVisibleColumns(tempVisibleColumns);
    localStorage.setItem("savedColumns", JSON.stringify(tempVisibleColumns));
    setIsSettingsOpen(false);
    // toast.success("Column settings saved successfully!");
  };

  const handleResetColumns = () => {
    const allColumnKeys = callbackTableColumns.map(col => col.key);
    setTempVisibleColumns(allColumnKeys);
    setVisibleColumns(allColumnKeys);
    localStorage.removeItem("savedColumns");
    setIsSettingsOpen(false);
  };

  const downloadCSV = () => {
    if (!filteredCbs || filteredCbs.length === 0) {
      toast.error("No data to download.");
      return;
    }

    if (!validColumns || validColumns.length === 0) {
      toast.error("No columns available for download.");
      return;
    }

    setIsDownloading(true);

    try {
      // Helper function to extract text content from callback data
      const getCellTextContent = (value: CallbackObjectType, dataIndex: string): string => {
        if (!value) return "-";
        
        try {
          if (dataIndex === "contactId") {
            return value.contactId || "-";
          }
          
          if (dataIndex === "requestStatus") {
            return value?.requestStatus + " " + (value?.callbackAttributes?.STATUS_MESSAGE ? "(" + value?.callbackAttributes?.STATUS_MESSAGE + ")" : "") || "-";
          }
          
          if (dataIndex === "customerName") {
            const isScheduledWeb = isScheduledWebCallback(value);
            if (isScheduledWeb) {
              return value?.callbackAttributes?.CUSTOMER_NAME || "-";
            }
            const firstName = value?.callbackAttributes?.FIRSTNAME || "";
            const lastName = value?.callbackAttributes?.LASTNAME || "";
            return `${firstName} ${lastName}`.trim() || "-";
          }
          
          if (dataIndex === "orgId") {
            return value?.callbackAttributes?.ORGID || value?.callbackAttributes?.ORG_ID || "-";
          }
          
          if (dataIndex === "connectContacts") {
            if (!value?.connectContacts?.length) return "-";
            return value.connectContacts.map((contact, index) => 
              `Attempt ${index + 1}: ${contact.id || ""}`
            ).join("; ");
          }
          
          if (dataIndex === "userLdap") {
            return value?.contactHandleBy || "-";
          }
          
          if (dataIndex === "callbackTimeEpoch") {
            return value?.callbackTimeEpoch ? new Date(value.callbackTimeEpoch * 1000).toLocaleString() : "-";
          }

          if (dataIndex === "callbackSurface") {
            return value?.callbackAttributes?.CALLBACK_SURFACE || "-";
          }

          if (dataIndex === "timezone") {
            return value?.callbackAttributes?.TIMEZONE || "-";
          }

          if (dataIndex === "caseId") {
            return value?.callbackAttributes?.CASEID || "-";
          }

          if (dataIndex === "acceptTimeEpoch") {
            return value?.callbackAttributes?.CALLBACK_ANSWERED_TIME ? 
              new Date(Number(value?.callbackAttributes?.CALLBACK_ANSWERED_TIME) * 1000).toLocaleString() : "-";
          }

          if (dataIndex === "dialTimeEpoch") {
            return value?.callbackAttributes?.CALLBACK_FIRST_ATTEMPT_TIME ? 
              new Date(Number(value?.callbackAttributes?.CALLBACK_FIRST_ATTEMPT_TIME) * 1000).toLocaleString() : "-";
          }

          if (dataIndex === "callbackScheduleTime") {
            return value?.callbackScheduleTimeEpoch ? 
              new Date(Number(value?.callbackScheduleTimeEpoch) * 1000).toLocaleString() : "-";
          }

          if (dataIndex === "productFamily") {
            return value?.productFamily || "-";
          }

          if (dataIndex === "customerAdobeId") {
            return value?.customerAdobeId || "-";
          }

          if (dataIndex === "phoneNumber") {
            const isScheduledWeb = isScheduledWebCallback(value);
            if (isScheduledWeb) {
              return value?.callbackAttributes?.CUSTOMER_WEB_MEETING_LINK || "-";
            }
            return value?.phoneNumber || "-";
          }

          if (dataIndex === "callbackType") {
            return getDisplayCallbackType(value);
          }

          if (dataIndex === "channel") {
            return value?.channel || "-";
          }

          if (dataIndex === "callbackAttempts") {
            return value?.callbackAttempts || "-";
          }

          if(dataIndex === "CRMQUEUE") {
            return value?.callbackAttributes?.CRMQUEUE || "-";
          }

          if(dataIndex === "ASSIGNED_ENGINEER_FULLNAME") {
            return value?.callbackAttributes?.ASSIGNED_ENGINEER_FULLNAME || "-";
          }

          if(dataIndex === "CALLBACK_REASSIGNED_TSE") {
            return value?.callbackAttributes?.CALLBACK_REASSIGNED_TSE_FULLNAME || value?.callbackAttributes?.ASSIGNED_ENGINEER_FULLNAME || "-";
          }

          if(dataIndex === "PRIORITYTEXT") {
            return value?.callbackAttributes?.PRIORITYTEXT || "-";
          }

          if(dataIndex === "entitlement") {
            return value?.callbackAttributes?.LEVEL || "-";
          }

          if(dataIndex === "ORGNAME") {
            return value?.callbackAttributes?.ORGNAME || "-";
          }

          if(dataIndex === "tseManagerName") {
            return value?.tseManagerName || "-";
          }

          if(dataIndex === "routingCurrentStatus") {
            return value?.routingCurrentStatus
              ? normalizeRoutingStatusText(value.routingCurrentStatus)
              : "-";
          }
          
          // Default case for other fields
          const fieldValue = value?.[dataIndex as keyof CallbackObjectType];
          if (fieldValue === undefined || fieldValue === null) return "-";
          return typeof fieldValue === 'object' ? JSON.stringify(fieldValue) : String(fieldValue);
        } catch (error) {
          console.error(`Error extracting text content for ${dataIndex}:`, error);
          return "-";
        }
      };

      // Create Excel file with proper column widths
      const worksheetData = [];
      
      // Define column widths for better Excel display
      const getColumnWidth = (columnTitle: string): number => {
        const widthMap: { [key: string]: number } = {
          "Product": 15,
          "Customer Name": 25,
          "Customer Email": 45,
          "Case Id": 12,
          "OrgId": 35,
          "Phone Number": 15,
          "Customer Timezone": 15,
          "Request Time": 20,
          "Accept Time": 20,
          "Dial Time": 20,
          "Callback Surface": 30,
          "Status": 12,
          "Call Attempts By": 20,
          "Call Attempts": 45,
          "Contact ID": 35,
          "Type": 12,
          "Callback Scheduled Time": 25
        };
        return widthMap[columnTitle] || 15; // Default width of 15
      };

      const exportColumns = [...validColumns];
      
      // Add header row (only for visible columns)
      const headerRow = exportColumns.map(col => col.title);
      worksheetData.push(headerRow);
      
      // Add data rows (only for visible columns)
      filteredCbs.forEach(item => {
        const row = exportColumns.map(col => {
          return getCellTextContent(item, col.dataIndex || "");
        });
        worksheetData.push(row);
      });
      
      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      
      // Set column widths
      const columnWidths = exportColumns.map(col => ({
        wch: getColumnWidth(col.title) // width in characters
      }));
      worksheet['!cols'] = columnWidths;
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Scheduled Callbacks");
      
      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      a.download = `scheduled_callbacks_${timestamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Excel file downloaded successfully! (${filteredCbs.length} records)`);
    } catch (error) {
      console.error("Error downloading Excel file:", error);
      toast.error("Failed to download Excel file. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const getStatusStats = () => {
    const stats = {
      total: filteredCbs.length,
      completed: filteredCbs.filter(cb => cb.requestStatus === "COMPLETED").length,
      inProgress: filteredCbs.filter(cb => cb.requestStatus === "INPROGRESS" || cb.requestStatus === "IN_PROGRESS").length,
      new: filteredCbs.filter(cb => cb.requestStatus === "NEW").length,
      failed: filteredCbs.filter(cb => cb.requestStatus?.startsWith("FAILED") || false).length,
      cancelled: filteredCbs.filter(cb => cb.requestStatus === "CANCELLED" as CallbackStatus).length,
      scheduledWeb: filteredCbs.filter(cb => `${cb.callbackType}` === "SCHEDULED_WEB").length,
    };
    return stats;
  };

  const stats = getStatusStats();

  return (
    <View UNSAFE_className={styles.container}>
      <>
        <Flex
          direction={"row"}
          justifyContent={"space-between"}
          UNSAFE_className={styles.header}    
        >
          <Flex direction={"row"} alignItems={"center"} gap={"size-250"}>
            <Timer
              getScheduledCallbackList={getScheduledCallbackList}
              isRotating={isRotating}
            />
          </Flex>
          <Flex direction={"row"} alignItems={"center"} gap={"size-250"}  UNSAFE_style={{ padding: "0px 24px 0px 0px" }}>
            <SearchField
              width="240px"
              placeholder="Filter by keyword"
              value={searchText}
              onChange={handleSearch}
            />

            {/* Download Excel */}
            <TooltipTrigger delay={0}>
              <ActionButton 
                onPress={downloadCSV} 
                aria-label="Download Excel"
                isDisabled={isDownloading}
                UNSAFE_className={styles.actionButton}
              >
                <Download UNSAFE_style={{ color: "white" }}/>
                <Text>{isDownloading ? "Generating..." : "Export To Excel"}</Text>
              </ActionButton>
              <Tooltip>Download data as Excel file</Tooltip>
            </TooltipTrigger>

            {/* Columns visibility settings */}
            <DialogTrigger isOpen={isSettingsOpen} onOpenChange={setIsSettingsOpen} type="popover">
              <TooltipTrigger delay={0}>
                <ActionButton 
                  onPress={handleSettingsOpenAndClose}
                  aria-label="Column Settings"
                  UNSAFE_className={styles.actionButton}
                >
                  <Settings UNSAFE_style={{ color: "white" }}/>
                </ActionButton>
                <Tooltip>Configure visible columns</Tooltip>
              </TooltipTrigger>
              <Dialog width="530px">
                <Content>
                  <View padding="size-10">
                    <CheckboxGroup
                      label={<Heading level={4}>Select columns to display</Heading>}
                      value={tempVisibleColumns}
                      onChange={handleColumnToggle}
                      isEmphasized
                    >
                      <Flex direction="row" wrap alignItems="start" justifyContent={"space-between"}>
                        {callbackTableColumns?.map((column) => (
                          <View key={column.key} minWidth="210px">
                            <Checkbox value={column.key}>
                              {column.title}
                            </Checkbox>
                          </View>
                        ))}
                      </Flex>
                    </CheckboxGroup>
                  </View>
                  <Divider size="S"/>
                  <View padding="size-200">
                    <Flex direction="row" justifyContent="end" gap="size-100">
                      <Button variant="secondary" onPress={handleResetColumns} isDisabled={tempVisibleColumns.length === 0}>
                        Reset to Default
                      </Button>
                      <Button variant="secondary" onPress={handleSettingsOpenAndClose}>
                        Cancel
                      </Button>
                      <Button variant="cta" onPress={handleSaveColumns} isDisabled={tempVisibleColumns.length === 0}>
                        Save
                      </Button>
                    </Flex>
                  </View>
                </Content>
              </Dialog>
            </DialogTrigger>
          </Flex>
        </Flex>

        <View UNSAFE_className={styles.content}>
          <Flex gap={"size-350"}
            direction={"row"}
            UNSAFE_style={{ padding: "10px 24px 0px 0px", margin:"0px 0px 10px 0px" }}
            justifyContent={"space-between"}
            alignItems={"center"}
            

          >
            {/* <Flex width={"120px"}>
              <Heading level={5}>
                Total callbacks: {filteredCbs.length}
              </Heading>
            </Flex> */}

            {/* <Flex alignItems="center" gap={"size-150"}*/}
              <DateRangePicker
                label="Date Range"
                labelAlign="start"
                labelPosition="top"
                width="250px"
                value={dateRange}
                onChange={handleDateChange}
                // maxValue={today(getLocalTimeZone())}
              />
              <Flex direction="row" gap="size-150">
                <Picker
                  label="Product"
                  labelAlign="start"
                  labelPosition="top"
                  items={cleanedProductList || fallbackProductList}
                  selectedKey={selectedProduct}
                  onSelectionChange={handleProductChange}
                  width="163px"
                >
                  {(product: any) => <Item key={product.id}>{product.name}</Item>}
                </Picker>
                <Picker
                  label="Entitlement"
                  labelAlign="start"
                  labelPosition="top"
                  items={entitlement}
                  selectedKey={selectedEntitlement}
                  onSelectionChange={handleEntitlementChange}
                  width="163px"
                >
                  {(entitlement) => <Item key={entitlement.id}>{entitlement.name}</Item>}
                </Picker>

                <Picker
                  label="Priority"
                  labelAlign="start"
                  labelPosition="top"
                  items={priority}
                  selectedKey={selectedPriority}
                  onSelectionChange={handlePriorityChange}
                  width="163px"
                >
                  {(priority) => <Item key={priority.id}>{priority.name}</Item>}
                </Picker>
                <Picker
                  label="Callback Status"
                  labelAlign="start"
                  labelPosition="top"
                  items={callbackStatus}
                  selectedKey={selectedCallbackStatus}
                  onSelectionChange={handleStatusChange}
                  width="163px"
                >
                  {(status) => <Item key={status.id}>{status.name}</Item>}
                </Picker>
                <Picker
                  label="Callback Type"
                  labelAlign="start"
                  labelPosition="top"
                  items={callbackTypes}
                  selectedKey={selectedCallbackType}
                  onSelectionChange={handleTypeChange}
                  width="163px"
                >
                  {(type) => <Item key={type.id}>{type.name}</Item>}
                </Picker>
                <Picker
                  label="Manager Name"
                  labelAlign="start"
                  labelPosition="top"
                  items={managerList}
                  selectedKey={selectedManager}
                  onSelectionChange={handleManagerChange}
                  width="163px"
                >
                  {(manager) => <Item key={manager.id}>{manager.name}</Item>}
                </Picker>
              {/* </Flex> */}
            </Flex>
          </Flex>

          <View UNSAFE_className={styles.mainCard} UNSAFE_style={{ marginBottom: "10px", padding: "0px", background: "white", borderRadius: "12px" }}>
            <Flex direction="row" justifyContent="space-between" alignItems="center" gap="size-0" wrap={false}>
              <Flex direction="row" alignItems="center" justifyContent="center" gap="size-150" UNSAFE_style={{ 
                flex: "1",
                padding: "8px 16px"
              }}>
                <View >
                  <img src={totalIcon} alt="Total"/>
                </View>
                <Flex direction="column" gap="size-0">
                  <Text UNSAFE_style={{ fontSize: "18px", fontWeight: "600", color: "#1f2937", lineHeight: "1.2" }}>{stats.total}</Text>
                  <Text UNSAFE_style={{ fontSize: "11px", color: "#6b7280", fontWeight: "400", whiteSpace: "nowrap" }}>Total Callbacks</Text>
                </Flex>
              </Flex>
              
              <View UNSAFE_style={{ width: "0.5px", background: "#e5e7eb", height: "32px" }} />
              
              <Flex direction="row" alignItems="center" justifyContent="center" gap="size-150" UNSAFE_style={{ 
                flex: "1",
                padding: "8px 16px"
              }}>
                <View>
                 <img src={newIcon} alt="New" />
                </View>
                <Flex direction="column" gap="size-0">
                  <Text UNSAFE_style={{ fontSize: "18px", fontWeight: "600", color: "#1f2937", lineHeight: "1.2" }}>{stats.new}</Text>
                  <Text UNSAFE_style={{ fontSize: "11px", color: "#6b7280", fontWeight: "400" }}>New</Text>
                </Flex>
              </Flex>

              <View UNSAFE_style={{ width: "1px", background: "#e5e7eb", height: "32px" }} />

              <Flex direction="row" alignItems="center" justifyContent="center" gap="size-150" UNSAFE_style={{ 
                flex: "1",
                padding: "8px 16px"
              }}>
                    <View>
                  <img src={inProgressIcon} alt="In Progress"/>
                </View>
                <Flex direction="column" gap="size-0">
                  <Text UNSAFE_style={{ fontSize: "18px", fontWeight: "600", color: "#1f2937", lineHeight: "1.2" }}>{stats.inProgress}</Text>
                  <Text UNSAFE_style={{ fontSize: "11px", color: "#6b7280", fontWeight: "400" }}>Inprogress</Text>
                </Flex>
              </Flex>

              <View UNSAFE_style={{ width: "1px", background: "#e5e7eb", height: "32px" }} />

              <Flex direction="row" alignItems="center" justifyContent="center" gap="size-150" UNSAFE_style={{ 
                flex: "1",
                padding: "8px 16px"
              }}>
                  <View >
                  <img src={completedIcon} alt="Completed"/>
                </View>
                <Flex direction="column" gap="size-0">
                  <Text UNSAFE_style={{ fontSize: "18px", fontWeight: "600", color: "#1f2937", lineHeight: "1.2" }}>{stats.completed}</Text>
                  <Text UNSAFE_style={{ fontSize: "11px", color: "#6b7280", fontWeight: "400" }}>Completed</Text>
                </Flex>
              </Flex>
              
              <View UNSAFE_style={{ width: "1px", background: "#e5e7eb", height: "32px" }} />
              
              <Flex direction="row" alignItems="center" justifyContent="center" gap="size-150" UNSAFE_style={{ 
                flex: "1",
                padding: "8px 16px"
              }}>
                <View >
                  <img src={failedIcon} alt="Failed"/>
                </View>
                <Flex direction="column" gap="size-0">
                  <Text UNSAFE_style={{ fontSize: "18px", fontWeight: "600", color: "#1f2937", lineHeight: "1.2" }}>{stats.failed}</Text>
                  <Text UNSAFE_style={{ fontSize: "11px", color: "#6b7280", fontWeight: "400" }}>Failed</Text>
                </Flex>
              </Flex>
              
              <View UNSAFE_style={{ width: "1px", background: "#e5e7eb", height: "32px" }} />
              
              <Flex direction="row" alignItems="center" justifyContent="center" gap="size-150" UNSAFE_style={{ 
                flex: "1",
                padding: "8px 16px"
              }}>
                <View >
                  <img src={cancelledIcon} alt="Cancelled"/>
                </View>
                <Flex direction="column" gap="size-0">
                  <Text UNSAFE_style={{ fontSize: "18px", fontWeight: "600", color: "#1f2937", lineHeight: "1.2" }}>{stats.cancelled}</Text>
                  <Text UNSAFE_style={{ fontSize: "11px", color: "#6b7280", fontWeight: "400" }}>Cancelled</Text>
                </Flex>
              </Flex>
              
              <View UNSAFE_style={{ width: "1px", background: "#e5e7eb", height: "32px" }} />
              
              <Flex direction="row" alignItems="center" justifyContent="center" gap="size-150" UNSAFE_style={{ 
                flex: "1",
                padding: "8px 16px",
                background: "linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%)",
                borderRadius: "8px",
                margin: "4px 8px 4px 16px"
              }}>
                <View UNSAFE_style={{ 
                  borderRadius: "6px",
                  padding: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  <img src={teamsIcon} alt="Web Meeting" style={{ width: "18px", height: "18px", transform: "scale(1.5)" }}/>
                </View>
                <Flex direction="column" gap="size-0">
                  <Text UNSAFE_style={{ fontSize: "18px", fontWeight: "600", color: "#6264A7", lineHeight: "1.2" }}>{stats.scheduledWeb}</Text>
                  <Text UNSAFE_style={{ fontSize: "11px", color: "#464775", fontWeight: "500" }}>Web Meeting</Text>
                </Flex>
              </Flex>
            </Flex>
          </View>
          
          {/* Only render the table if we have valid columns */}
          {validColumns.length > 0 ? (
            <TableView
              aria-label="scheduled-callbacks"
              height="62vh"
              UNSAFE_style={{
                padding: "0px 24px 0px 0px",
                scrollbarWidth: "thin",
              }}
              UNSAFE_className={styles.tableHeader}
              overflowMode="wrap"
              selectionMode="single"
              selectionStyle="highlight"
              renderEmptyState={() => (
                <View UNSAFE_className={styles.emptyState}>
                  <Text>No data available</Text>
                </View>
              )}
            >
              <TableHeader>
                {validColumns.map((column) => {return column.dataIndex === 'contactId' ? (
                  <Column 
                    key={column.dataIndex || Math.random().toString()} 
                    //minWidth={column.width as ColumnStaticSize} 
                    allowsResizing
                    // maxWidth={column.width as ColumnStaticSize}
                    // width={column.width as ColumnStaticSize}
                    minWidth={column.width as ColumnStaticSize}
                    // wrap={true}
                  >
                    {column.title || ""}
                  </Column>
                ):
                <Column 
                  key={column.dataIndex || Math.random().toString()} 
                  minWidth={column.width as ColumnStaticSize} 
                  allowsResizing
                >
                  {column.title || ""}
                </Column>
                })}
              </TableHeader>

              <TableBody>
                {
                  filteredCbs && filteredCbs.length > 0 
                    ? filteredCbs.map((value: CallbackObjectType, index) => (
                        <Row key={value?.contactId || `row-${index}`}>
                          {validColumns.map((column) => (
                            <Cell key={`${value?.contactId || index}-${column.dataIndex || index}`} >
                              {renderCellContent(value, column.dataIndex || "", handleRescheduleClick, handleCancelClick, userEmail)}
                            </Cell>
                          ))}
                        </Row>
                      ))
                    : []
                }
              </TableBody>
            </TableView>
          ) : (
            <View UNSAFE_className={styles.emptyState}>
              <Text>Loading table columns...</Text>
            </View>
          )}
        </View>

        {/* Reassign Callback Dialog */}
        {/* <ReassignCallbackDialog
          isOpen={isReassignDialogOpen}
          onClose={handleReassignDialogClose}
          callbackData={selectedCallbackForReassign}
          onReassign={handleReassignConfirm}
          userEmail={userEmail}
        /> */}

        {/* Reschedule Callback Dialog */}
        <RescheduleCallbackDialog
          isOpen={isRescheduleDialogOpen}
          onClose={handleRescheduleDialogClose}
          callbackData={selectedCallbackForReschedule}
          onReschedule={handleRescheduleConfirm}
          timeslots={timeslots}
          userEmail={userEmail}
          userName={userName}
        />

        {/* Cancel Confirmation Dialog */}
        <CancelConfirmationDialog
          isOpen={showCancelConfirmDialog}
          onClose={handleCancelConfirmDialogClose}
          callbackData={selectedCallbackForCancel}
          onCancelSuccess={getScheduledCallbackList}
          userEmail={userEmail}
          userName={userName}
        />


      </>
    </View>
  );
};

export default React.memo(CallbackDashboard);